import {apolloPlatformAxiosInstance as axiosInstance} from './apolloPlatformAxiosInstance';

export type UserGroup = {
  id: number;
  name: string;
  default_group: string;
  allowed_domains: string | null;
  group_type: string;
};

interface Permission {
  key: string;
  description: string | null;
}

export type Role = {
  id: number;
  name: string;
  permissions: Permission[];
  type:
    | 'standard'
    | 'ekco_employee'
    | 'staff'
    | 'admin'
    | 'customer' /** @see {@link apollo_platform/apps/authentication/models/Role.Type} */;
  is_direct: boolean | null;
};

export type User = {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar: string | null;
  is_staff: boolean;
  is_superuser: boolean;
  class_name: string;
  groups: UserGroup[];
  USER_FEEDBACK_API_KEY: string;
  PRODUCT_CATALOGUE_API: string;
  BACKEND_ADMIN: string;
  fresh_requester_id: number | null;
};

export type UserPermissions = {
  user_permissions: string[];
};

export const apiGetUser = async () => {
  return await axiosInstance.get('users/me');
};

export const apiGetEkcoUsers = async () => {
  return await axiosInstance.get<EkcoUserWithRoles[]>('ekco-users/');
};

export const apiGetEkcoContactUsers = async () => {
  return await axiosInstance.get<EkcoUserWithRoles[]>('ekco-users/', {
    params: {is_contact: true},
  });
};

export const apiGetUserRoles = async () => {
  return await axiosInstance.get('users/roles');
};

export const apiPostUserRoleAssignments = async (id: number, roleIds: number[]) => {
  return await axiosInstance.post(`users/${id}/add_roles`, roleIds);
};

export const apiDeleteUserRoleAssignment = async (id: number, roleId: number) => {
  return await axiosInstance.delete(`users/${id}/remove_role/${roleId}`);
};

export const apiDeleteUserCustomerRelation = async (id: number, customerId: number) => {
  return await axiosInstance.put(`users/${id}/remove_customer`, {
    customer_id: customerId,
  });
};

export const apiGetCustomerRoles = async () => {
  return await axiosInstance.get('permissions/roles/');
};

export type EkcoUserWithRoles = {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  roles: Role[];
};

export const apiGetEkcoUsersWithRoles = async (search?: string) =>
  await axiosInstance.get<EkcoUserWithRoles[]>('ekco-users/', {params: {search}});

export const apiGetEkcoEmployeeRoles = async () =>
  await axiosInstance.get<Role[]>('ekco-users/available_roles/');

export const apiAddDirectRoleAssignments = async (userId: number, roleIds: number[]) =>
  await axiosInstance.post<EkcoUserWithRoles>(`ekco-users/${userId}/add_roles/`, roleIds);

export const apiRemoveDirectRoleAssignments = async (userId: number, roleIds: number[]) =>
  await axiosInstance.post<EkcoUserWithRoles>(`ekco-users/${userId}/remove_roles/`, roleIds);

export const apiDeactivateEkcoUser = async (userId: number) =>
  await axiosInstance.delete(`ekco-users/${userId}/`);
